import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Brain, Play } from "lucide-react";
import GalleryCarousel from "../components/ScreenshotCarousel";
import FeatureShowcase from "../components/FeatureShowcase";
import PageMeta from "../components/PageMeta";
import {
  VoiceWaveform, AIInsightCard, ModuleFlowDiagram, LiveDashboardPulse,
  GPSTrackingVisual, OCRScanVisual, GanttChartVisual, LeaderboardVisual,
  CVExtractionVisual, JobCardFlowVisual, BadgesRewardsVisual,
} from "../components/VisualElements";
import { MODULE_DATA, SLUG_TO_NAME } from "../data/moduleData";
import { useCurrency, useModulePricing } from "../context/CurrencyContext";
import { DEMO_URL } from "../lib/urls";

const VISUAL_MAP = {
  gantt: <GanttChartVisual />,
  leaderboard: <LeaderboardVisual />,
  "cv-extraction": <CVExtractionVisual />,
  "jobcard-flow": <JobCardFlowVisual />,
  "badges-rewards": <BadgesRewardsVisual />,
};

export default function ModulePage({ onTrialClick }) {
  const { module } = useParams();
  const data = MODULE_DATA[module];
  const { symbol, rate } = useCurrency();
  const localPricing = useModulePricing(module);
  const cx = (usd) => Math.round(usd * rate);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display font-bold text-2xl text-white mb-4">Module not found</h1>
          <Link to="/space/rogue" className="text-rogue-cyan hover:underline">Back to Rogue</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={`module-page-${module}`}>
      <PageMeta
        title={`Rogue ${SLUG_TO_NAME[module]}`}
        description={data.tagline}
        path={`/space/rogue/${module}`}
      />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-2 text-sm text-geminii-dim mb-6">
            <Link to="/space" className="hover:text-white transition-colors">Space</Link>
            <span>/</span>
            <Link to="/space/rogue" className="hover:text-white transition-colors">Rogue</Link>
            <span>/</span>
            <span className="text-rogue-cyan">{SLUG_TO_NAME[module]}</span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-4 mb-4"
          >
            <data.icon size={32} className="text-rogue-cyan" />
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white">{data.name}</h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="text-lg text-geminii-muted max-w-2xl mb-2"
          >
            {data.tagline}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="text-sm text-geminii-dim max-w-2xl leading-relaxed"
          >
            {data.heroDescription}
          </motion.p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              onClick={onTrialClick}
              data-testid={`module-${module}-cta`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
            >
              Start free trial — no credit card
              <ArrowRight size={16} />
            </button>
            <Link
              to="/space/rogue/case-study"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
            >
              See the case study
            </Link>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`module-${module}-demo`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
            >
              <Play size={14} className="fill-rogue-cyan" />
              View live demo
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Carousel */}
      {data.gallery && data.gallery.length > 0 && (
        <section className="py-10 md:py-14 border-t border-white/[0.04]">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
            <GalleryCarousel screenshots={data.gallery} />
          </div>
        </section>
      )}

      {/* Visual Elements (module-specific) */}
      {module === "cerebrum" && (
        <section className="py-10 md:py-14 border-t border-white/[0.04]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <VoiceWaveform />
              <AIInsightCard />
            </div>
          </div>
        </section>
      )}
      {module === "sales-flow" && (
        <section className="py-12 md:py-16 border-t border-white/[0.04]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ModuleFlowDiagram />
              <LiveDashboardPulse />
            </div>
          </div>
        </section>
      )}
      {module === "ops" && (
        <section className="py-12 md:py-16 border-t border-white/[0.04]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            <GPSTrackingVisual />
          </div>
        </section>
      )}
      {module === "stockroom" && (
        <section className="py-12 md:py-16 border-t border-white/[0.04]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            <OCRScanVisual />
          </div>
        </section>
      )}
      {module === "projects" && (
        <section className="py-12 md:py-16 border-t border-white/[0.04]">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
            <GanttChartVisual />
          </div>
        </section>
      )}
      {module === "teams" && (
        <section className="py-12 md:py-16 border-t border-white/[0.04]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <LeaderboardVisual />
              <BadgesRewardsVisual />
            </div>
            <CVExtractionVisual />
          </div>
        </section>
      )}

      {/* Deep Feature Showcase */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            What {SLUG_TO_NAME[module]} actually does
          </h2>
          <p className="text-geminii-muted mb-14 max-w-xl">
            Every capability below is live in the app today — click any screenshot to expand it.
          </p>
          <FeatureShowcase
            features={data.features.map((f) =>
              f.useVisual ? { ...f, visual: VISUAL_MAP[f.useVisual] } : f
            )}
          />
        </div>
      </section>

      {/* Cerebrum AI */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-gradient-to-br from-cerebrum-violet/5 to-transparent border border-cerebrum-violet/15 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Brain size={28} className="text-cerebrum-violet" />
              <h2 className="font-display font-bold text-2xl text-white">Cerebrum AI inside {SLUG_TO_NAME[module]}</h2>
            </div>
            <p className="text-geminii-muted max-w-2xl leading-relaxed">{data.cerebrum}</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl text-white mb-2">Pricing for {SLUG_TO_NAME[module]}</h2>
          <p className="text-geminii-muted text-sm mb-8">Per user/month, or get all 8 modules with the All Rogue package.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { tier: "Starter", users: "1–5 users", price: 9, intro: 5 },
              { tier: "Growth", users: "6–15 users", price: 15, intro: 8, popular: true },
              { tier: "Business", users: "16–50 users", price: 24, intro: 12 },
            ].map((p) => (
              <div key={p.tier} className={`bg-geminii-card border rounded-xl p-6 text-center ${p.popular ? "border-rogue-cyan/30" : "border-white/[0.06]"}`}>
                {p.popular && <span className="text-[10px] font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-0.5 rounded mb-2 inline-block">Most popular</span>}
                <h3 className="font-display font-bold text-sm text-white mb-1">{p.tier}</h3>
                <p className="text-xs text-geminii-dim mb-4">{p.users}</p>
                <p className="text-sm text-geminii-dim line-through mb-1">{symbol} {cx(p.price).toLocaleString()}/user/mo</p>
                <p className="font-display font-bold text-3xl text-rogue-cyan">{symbol} {cx(p.intro).toLocaleString()}</p>
                <p className="text-xs text-geminii-dim">per user/mo — first 3 months</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link to="/space/rogue/pricing" className="text-rogue-cyan text-sm font-medium hover:underline flex items-center gap-1">
              See All Rogue package pricing <ArrowRight size={14} />
            </Link>
            <span className="text-xs text-geminii-dim">Prices excl. VAT. Multi-currency billing available.</span>
          </div>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-xl text-white mb-8">Pairs perfectly with</h2>
          <div className="flex flex-wrap gap-4">
            {data.crossSell.map((slug) => (
              <Link
                key={slug}
                to={`/space/rogue/${slug}`}
                className="flex items-center gap-2 px-5 py-3 rounded-lg bg-geminii-card border border-white/[0.06] hover:border-rogue-cyan/30 text-sm text-white font-medium transition-all duration-250"
              >
                Rogue {SLUG_TO_NAME[slug]}
                <ArrowRight size={14} className="text-geminii-dim" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
